<template>
  <div
    class="demo-step"
    :class="{ 'is-final-step': isFinalStep }"
  >
    <!-- Step Header -->
    <div class="demo-step__header">
      <h2
        class="demo-step__title"
        :title="step.title"
      >
        {{ step.title }}
      </h2>
      <span
        class="demo-step__counter"
        aria-label="Step counter"
      >
        Step {{ currentStepNumber }} of {{ totalSteps }}
      </span>
    </div>

    <!-- Step Content -->
    <div class="demo-step__content">
      <p class="demo-step__description">
        {{ step.description }}
      </p>

      <!-- Completion Message (for view-only steps) -->
      <div
        v-if="step.action === 'view'"
        class="demo-step__completion"
      >
        ✓ Ready to continue
      </div>
    </div>
  </div>
</template>

<script>
/**
 * DemoStep Component
 * Displays step content, title, description, and instruction
 * 
 * @component
 * @example
 * <DemoStep 
 *   :step="stepObject" 
 *   :currentStepIndex="2"
 *   :totalSteps="13"
 * />
 */
export default {
  name: 'DemoStep',

  props: {
    /**
     * Step configuration object
     * Contains all step content and configuration
     */
    step: {
      type: Object,
      required: true,
      validator: (value) => {
        return (
          value.id &&
          value.title &&
          value.description &&
          typeof value.stepNumber === 'number'
        );
      },
    },

    /**
     * Current step index (0-based)
     */
    currentStepIndex: {
      type: Number,
      required: true,
      validator: (value) => value >= 0,
    },

    /**
     * Total number of steps
     */
    totalSteps: {
      type: Number,
      required: true,
      validator: (value) => value > 0,
    },
  },

  computed: {
    /**
     * Current step number (1-based for display)
     */
    currentStepNumber() {
      return this.currentStepIndex + 1;
    },

    /**
     * Check if this is the final step
     */
    isFinalStep() {
      return this.currentStepIndex === this.totalSteps - 1;
    },

    /**
     * Calculate progress percentage
     */
    progressPercentage() {
      return (this.currentStepNumber / this.totalSteps) * 100;
    },
  },

  mounted() {
    /**
     * Announce step change to screen readers
     */
    this.announceStepChange();
  },

  updated() {
    /**
     * Re-announce if step content changes
     */
    this.announceStepChange();
  },

  methods: {
    /**
     * Announce current step to screen readers for accessibility
     */
    announceStepChange() {
      const message = `Step ${this.currentStepNumber} of ${this.totalSteps}: ${this.step.title}${this.step.instruction ? '. ' + this.step.instruction : ''}`;
      
      // Announce to screen readers if available
      if (this.$announce) {
        this.$announce(message);
      } else {
        // Fallback: log to console for debugging
        console.log('[Demo Announcement]', message);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.demo-step {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background-color: #1e1e2e;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  animation: demo-step-enter 300ms ease-out;

  @media (max-width: 480px) {
    padding: 12px;
    gap: 12px;
  }
}

@keyframes demo-step-enter {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.demo-step__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.demo-step__title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #e0e0e0;
  line-height: 1.4;
  flex: 1;

  @media (max-width: 480px) {
    font-size: 18px;
  }
}

.demo-step__counter {
  font-size: 12px;
  font-weight: 400;
  color: #888888;
  white-space: nowrap;
  padding: 4px 8px;
  background-color: #2a2a3e;
  border-radius: 4px;

  @media (max-width: 480px) {
    align-self: flex-end;
  }
}

.demo-step__content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.demo-step__description {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: #a0a0b0;
  line-height: 1.6;
}

.demo-step__instruction {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: #1a2640;
  border-left: 4px solid #4da3ff;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #c8d8f0;
}

.demo-step__instruction-icon {
  width: 20px;
  height: 20px;
  color: #4da3ff;
  flex-shrink: 0;
}

.demo-step__completion {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: #162a1e;
  border-left: 4px solid #22c55e;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #4ade80;
}

.demo-step.is-final-step {
  .demo-step__description::after {
    content: ' 🎉';
  }
}

.demo-step__progress {
  padding-top: 4px;
}

.demo-step__progress-bar {
  width: 100%;
  height: 4px;
  background-color: #2a2a3e;
  border-radius: 2px;
  overflow: hidden;
  display: flex;
}

.demo-step__progress-fill {
  height: 100%;
  background-color: #4da3ff;
  transition: width 300ms ease-in-out;
}
</style>
